/**
 * Cases AI-Search Modal (#1232)
 * Semantische Suche über Cases pro Kunde via Embeddings.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { X, Sparkles, Search, ChevronRight, Building } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';
const MODULE_COLOR = '#338edc';

async function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  const r = await fetch(`${API}${path}`, {
    credentials: 'include',
    ...opts,
    headers: { ...headers, ...opts.headers },
    body: hasBody && typeof opts.body !== 'string' ? JSON.stringify(opts.body) : opts.body,
  });
  if (!r.ok) {
    const text = await r.text();
    let err;
    try { err = JSON.parse(text); } catch { err = { error: text }; }
    throw new Error(err.message || err.error || `HTTP ${r.status}`);
  }
  return r.json();
}

function ScoreBadge({ score }) {
  const cls = score >= 80
    ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-500/15 dark:border-green-500/30 dark:text-green-400'
    : score >= 60
    ? 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-500/15 dark:border-yellow-500/30 dark:text-yellow-400'
    : 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/15 dark:border-orange-500/30 dark:text-orange-400';
  return (
    <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls} min-w-[44px]`}>
      {score}%
    </span>
  );
}

function StageBadge({ stage }) {
  const map = {
    waiting_for_support: 'bg-blue-50 text-blue-700 border-blue-200',
    in_progress: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    clarification_with_recipient: 'bg-amber-50 text-amber-700 border-amber-200',
    solution_proposed: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    reopened: 'bg-red-50 text-red-700 border-red-200',
    closed: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  const cls = map[stage] || 'bg-gray-100 text-gray-700 border-gray-200';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}>{stage || '—'}</span>;
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export default function AISearchModal({ open, onClose, onSelectCase }) {
  const { t } = useLanguage();
  const [kundenList, setKundenList] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOpen, setCustomerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [error, setError] = useState(null);
  const queryRef = useRef(null);
  const customerInputRef = useRef(null);
  const customerWrapRef = useRef(null);
  const debouncedCustomerSearch = useDebounce(customerSearch, 200);

  useEffect(() => {
    if (!open) return;
    setError(null);
    fetch(`${API}/api/kunden`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data?.kunden || []);
        setKundenList(list.filter(k => k.active !== false));
      })
      .catch(() => setKundenList([]));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setCustomerId('');
      setCustomerSearch('');
      setCustomerOpen(false);
      setQuery('');
      setResults(null);
      setMetadata(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!customerOpen) return;
    const onDown = (e) => {
      if (customerWrapRef.current && !customerWrapRef.current.contains(e.target)) {
        setCustomerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [customerOpen]);

  const filteredCustomers = useMemo(() => {
    const q = debouncedCustomerSearch.trim().toLowerCase();
    if (!q) return kundenList.slice(0, 20);
    const matches = [];
    for (const k of kundenList) {
      const name = (k.name || '').toLowerCase();
      const idx = name.indexOf(q);
      if (idx < 0) continue;
      matches.push({ k, rank: idx === 0 ? 0 : 1, idx, name });
    }
    matches.sort((a, b) => a.rank - b.rank || a.idx - b.idx || a.name.localeCompare(b.name));
    return matches.slice(0, 20).map(m => m.k);
  }, [kundenList, debouncedCustomerSearch]);

  const selectedCustomer = useMemo(
    () => kundenList.find(k => Number(k.id) === Number(customerId)),
    [kundenList, customerId]
  );

  const pickCustomer = (k) => {
    setCustomerId(k.id);
    setCustomerOpen(false);
    setCustomerSearch('');
    setTimeout(() => queryRef.current?.focus(), 50);
  };

  const handleCustomerKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const first = filteredCustomers[0];
      if (first) pickCustomer(first);
    } else if (e.key === 'Escape') {
      setCustomerOpen(false);
      e.currentTarget.blur();
    }
  };

  const handleSearch = async () => {
    if (!customerId || !query.trim()) return;
    setLoading(true);
    setError(null);
    setResults(null);
    setMetadata(null);
    try {
      const res = await api('/api/cases/search/semantic', {
        method: 'POST',
        body: { customer_id: Number(customerId), query: query.trim(), limit: 10 },
      });
      setResults(res.results || []);
      setMetadata(res.metadata || {});
    } catch (err) {
      setError(err.message || 'Suche fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSearch();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-12 pb-8 px-4 bg-black/40 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div
        className="bg-surface-card border border-line rounded-card shadow-xl w-full max-w-3xl flex flex-col max-h-[calc(100vh-5rem)]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md" style={{ backgroundColor: `${MODULE_COLOR}15` }}>
              <Sparkles className="w-4 h-4" style={{ color: MODULE_COLOR }} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-content">{t('cases.aiSearch.title')}</h2>
              <p className="text-[11px] text-content-tertiary">{t('cases.aiSearch.subtitle')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-surface-hover text-content-tertiary">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step 1: Customer — außerhalb der Scroll-Body, damit das Dropdown nicht vom overflow-y-auto abgeschnitten wird (#1234) */}
        <div className="px-5 pt-4 relative z-20">
          <label className="block">
            <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('cases.aiSearch.customerLabel')}</span>
            <div className="relative mt-1.5" ref={customerWrapRef}>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-tertiary pointer-events-none" />
                <input
                  ref={customerInputRef}
                  type="text"
                  value={customerOpen ? customerSearch : (selectedCustomer?.name || customerSearch)}
                  onChange={e => { setCustomerSearch(e.target.value); setCustomerOpen(true); if (customerId) setCustomerId(''); }}
                  onFocus={() => setCustomerOpen(true)}
                  onKeyDown={handleCustomerKeyDown}
                  placeholder={t('cases.aiSearch.customerPlaceholder')}
                  className="w-full pl-9 pr-9 py-2 border border-line rounded-card bg-surface-inset text-sm focus:outline-none focus:border-primary-500/50"
                  autoComplete="off"
                />
                <ChevronRight
                  onClick={() => { setCustomerOpen(o => !o); customerInputRef.current?.focus(); }}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary cursor-pointer transition-transform ${customerOpen ? 'rotate-90' : ''}`}
                />
              </div>
              {customerOpen && (
                <div className="absolute z-30 mt-1 w-full bg-surface-card border border-line rounded-card shadow-lg max-h-80 overflow-y-auto">
                  {filteredCustomers.length === 0 && (
                    <div className="px-3 py-2 text-xs text-content-tertiary">{t('cases.aiSearch.noCustomers')}</div>
                  )}
                  {filteredCustomers.map((k, i) => (
                    <button
                      key={k.id}
                      type="button"
                      onMouseDown={e => { e.preventDefault(); pickCustomer(k); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-hover text-content flex items-center gap-2 ${i === 0 && customerSearch.trim() ? 'bg-surface-hover' : ''}`}
                    >
                      <Building className="w-3.5 h-3.5 text-content-tertiary" />
                      {k.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </label>
        </div>

        {/* Body */}
        <div className="px-5 pb-4 flex-1 overflow-y-auto min-h-0">
          {/* Step 2: Query */}
          {customerId && (
            <label className="block mt-4">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('cases.aiSearch.queryLabel')}</span>
              <textarea
                ref={queryRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('cases.aiSearch.queryPlaceholder')}
                rows={3}
                className="mt-1.5 w-full px-3 py-2 border border-line rounded-card bg-surface-inset text-sm resize-none focus:outline-none focus:border-primary-500/50"
              />
              <span className="block mt-1 text-[11px] text-content-tertiary">
                {t('cases.aiSearch.queryHint')}
              </span>
            </label>
          )}

          {/* Search button */}
          {customerId && (
            <button
              onClick={handleSearch}
              disabled={!customerId || !query.trim() || loading}
              className="mt-4 w-full px-4 py-2.5 rounded-card text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40 transition-opacity flex items-center justify-center gap-2"
              style={{ backgroundColor: MODULE_COLOR }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  {t('cases.aiSearch.searching')}
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  {t('cases.aiSearch.searchButton')}
                </>
              )}
            </button>
          )}

          {/* Error */}
          {error && (
            <div className="mt-4 px-3 py-2 rounded-card bg-red-50 border border-red-200 text-sm text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Results */}
          {results && results.length === 0 && !loading && (
            <div className="mt-4 px-3 py-6 rounded-card bg-surface-inset border border-line text-center text-sm text-content-tertiary">
              {t('cases.aiSearch.noResults')}
            </div>
          )}

          {results && results.length > 0 && (
            <div className="mt-4 space-y-2">
              <div className="text-[11px] text-content-tertiary px-1">
                {t('cases.aiSearch.resultsHeader', { n: results.length })}
                {metadata?.embedding_time_ms != null && (
                  <span className="ml-2">({((metadata.embedding_time_ms + (metadata.knn_time_ms || 0) + (metadata.summarization_time_ms || 0)) / 1000).toFixed(1)}s)</span>
                )}
              </div>
              {results.map(r => (
                <button
                  key={r.case_id}
                  type="button"
                  onClick={() => { onSelectCase?.(r); onClose(); }}
                  className="w-full text-left px-4 py-3 rounded-card border border-line bg-surface-inset hover:border-primary-500/40 hover:bg-surface-hover transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <ScoreBadge score={r.score} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-mono text-content-tertiary">
                          {r.case_source === 'oms' ? 'OMS' : 'Mail'}-#{r.case_number}
                        </span>
                        <StageBadge stage={r.stage} />
                        {r.created_at && (
                          <span className="text-[10px] text-content-tertiary">
                            {new Date(r.created_at).toLocaleDateString('de-AT')}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-semibold text-content mt-1 truncate">{r.title || '—'}</div>
                      {r.summary && (
                        <div className="text-xs text-content-secondary mt-1.5 font-medium">{r.summary}</div>
                      )}
                      {r.quote && (
                        <div className="text-[11px] text-content-tertiary italic mt-1.5 line-clamp-2">
                          „{r.quote}"
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Empty state hint */}
          {!customerId && (
            <div className="mt-6 px-4 py-6 rounded-card bg-surface-inset border border-line border-dashed text-center text-sm text-content-tertiary">
              {t('cases.aiSearch.hintCustomerFirst')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-line bg-surface-inset rounded-b-card flex items-center justify-between text-[11px] text-content-tertiary">
          <span>{t('cases.aiSearch.footer')}</span>
          <button onClick={onClose} className="px-3 py-1 text-content hover:opacity-70">{t('cases.aiSearch.close')}</button>
        </div>
      </div>
    </div>
  );
}
